import {
  AlertTriangle,
  ArrowLeftRight,
  Banknote,
  BarChart3,
  BellRing,
  BookMarked,
  BookOpen,
  Boxes,
  Briefcase,
  Building2,
  Calculator,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  CalendarRange,
  CalendarSearch,
  Car,
  CarFront,
  ChartLine,
  CheckCheck,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Cog,
  Coins,
  Contact,
  CreditCard,
  Download,
  FileCheck,
  FileInput,
  FilePen,
  FileSearch,
  FileSignature,
  FileSpreadsheet,
  FileStack,
  FileText,
  FileType,
  Files,
  Filter,
  FolderKanban,
  FolderOpen,
  GanttChart,
  Globe,
  GraduationCap,
  Hammer,
  Handshake,
  HardHat,
  HistoryIcon,
  Images,
  Inbox,
  Kanban,
  KanbanSquare,
  Landmark,
  LayoutDashboard,
  LayoutTemplate,
  Library,
  LinkIcon,
  ListIcon,
  ListChecks,
  ListOrdered,
  MapIcon,
  MessageSquare,
  MonitorSmartphone,
  Package,
  PackageCheck,
  PenTool,
  PieChart,
  Plane,
  QrCode,
  Receipt,
  Route,
  Scale,
  ScanText,
  SearchCheck,
  Share2,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  SquarePen,
  Timer,
  Tractor,
  TrendingUp,
  Truck,
  UserCheck,
  Users,
  UsersRound,
  Wallet,
  Warehouse,
  Wrench,
  type LucideIcon,
} from "lucide-react";

export const SHOWCASE_DEVICES = ["mobile", "desktop"] as const;
export type ShowcaseDevice = (typeof SHOWCASE_DEVICES)[number];

/** Pixel size of the stored screenshots, so the frames reserve space before they load. */
export const SHOWCASE_SCREEN_SIZE: Record<
  ShowcaseDevice,
  { width: number; height: number }
> = {
  mobile: { width: 585, height: 1266 },
  desktop: { width: 1200, height: 750 },
};

export interface ShowcaseScreenDef {
  icon: LucideIcon;
  /** Devices with a real Bidmio screenshot of this screen (see public/images/showcase/SOURCES.md). */
  devices: readonly ShowcaseDevice[];
}

const BOTH: readonly ShowcaseDevice[] = ["mobile", "desktop"];
const DESKTOP: readonly ShowcaseDevice[] = ["desktop"];

/**
 * Every app screen the showcase can show. Copy lives under
 * `sections.productShowcase.screens.<id>`; screenshots are
 * public/images/showcase/<lang>/<id>-<device>.webp.
 */
export const SHOWCASE_SCREENS = {
  "time-tracking": { icon: Clock, devices: BOTH },
  projects: { icon: FolderKanban, devices: BOTH },
  planning: { icon: CalendarDays, devices: BOTH },
  quality: { icon: ClipboardCheck, devices: BOTH },
  materials: { icon: Package, devices: BOTH },
  expenses: { icon: Receipt, devices: BOTH },
  diary: { icon: BookOpen, devices: BOTH },
  invoicing: { icon: FileText, devices: BOTH },
  customer: { icon: Users, devices: BOTH },
  gantt: { icon: GanttChart, devices: DESKTOP },
  kanban: { icon: Kanban, devices: DESKTOP },
  unbilled: { icon: FileCheck, devices: BOTH },
  quote: { icon: FileText, devices: BOTH },
  "invoice-pdf": { icon: FileText, devices: DESKTOP },
  receivables: { icon: Coins, devices: BOTH },
  aging: { icon: ListOrdered, devices: DESKTOP },
  bank: { icon: Landmark, devices: BOTH },
  accounting: { icon: ChartLine, devices: BOTH },
  ocr: { icon: ScanText, devices: BOTH },
  dashboard: { icon: LayoutDashboard, devices: BOTH },
  "project-stats": { icon: BarChart3, devices: BOTH },
  "budget-vs-actuals": { icon: Scale, devices: DESKTOP },
  "forecast-eac": { icon: TrendingUp, devices: BOTH },
  "project-economy": { icon: Wallet, devices: BOTH },
  "change-orders": { icon: FilePen, devices: BOTH },
  "work-summaries": { icon: FileSpreadsheet, devices: DESKTOP },
  "boq-budget": { icon: Calculator, devices: DESKTOP },
  "quotes-list": { icon: Files, devices: DESKTOP },
  "quote-pdf": { icon: FileType, devices: DESKTOP },
  "hr-timetracking": { icon: Timer, devices: DESKTOP },
  "workzone-home": { icon: Smartphone, devices: BOTH },
  "attendance-qr": { icon: QrCode, devices: BOTH },
  "crm-pipeline": { icon: Filter, devices: DESKTOP },
  inquiries: { icon: Inbox, devices: BOTH },
  "crm-follow-ups": { icon: BellRing, devices: DESKTOP },
  employees: { icon: Contact, devices: DESKTOP },
  crews: { icon: UsersRound, devices: DESKTOP },
  leaves: { icon: Plane, devices: BOTH },
  "workzone-requests": { icon: MessageSquare, devices: BOTH },
  "fleet-overview": { icon: MapIcon, devices: DESKTOP },
  "fleet-vehicles": { icon: Car, devices: BOTH },
  "fleet-trips": { icon: Route, devices: DESKTOP },
  "fleet-events": { icon: AlertTriangle, devices: DESKTOP },
  "vehicle-detail": { icon: CarFront, devices: BOTH },
  "stock-overview": { icon: Boxes, devices: DESKTOP },
  "stock-locations": { icon: Warehouse, devices: BOTH },
  "stock-movements": { icon: ArrowLeftRight, devices: DESKTOP },
  "stock-counts": { icon: ClipboardList, devices: BOTH },
  "reorder-suggestions": { icon: ShoppingCart, devices: BOTH },
  "purchase-orders": { icon: ShoppingBag, devices: DESKTOP },
  "calendar-events": { icon: CalendarPlus, devices: DESKTOP },
  "project-calendar": { icon: CalendarRange, devices: BOTH },
  inspections: { icon: SearchCheck, devices: BOTH },
  "bozp-overview": { icon: ShieldCheck, devices: BOTH },
  "bozp-trainings": { icon: GraduationCap, devices: DESKTOP },
  "qc-project": { icon: ListChecks, devices: BOTH },
  "on-site": { icon: HardHat, devices: BOTH },
  "install-app": { icon: Download, devices: BOTH },
  "bank-accounts": { icon: CreditCard, devices: BOTH },
  "bank-connections": { icon: LinkIcon, devices: BOTH },
  "bank-import-history": { icon: HistoryIcon, devices: BOTH },
  payroll: { icon: Banknote, devices: BOTH },
  "machines-overview": { icon: Tractor, devices: BOTH },
  "machine-planning": { icon: CalendarClock, devices: BOTH },
  "machine-detail": { icon: Cog, devices: BOTH },
  "tool-maintenance": { icon: Wrench, devices: BOTH },
  "tool-catalog": { icon: BookMarked, devices: DESKTOP },
  "tools-dashboard": { icon: Hammer, devices: BOTH },
  "service-today": { icon: CalendarCheck, devices: BOTH },
  "service-board": { icon: KanbanSquare, devices: BOTH },
  "service-week": { icon: CalendarDays, devices: BOTH },
  "service-van": { icon: Truck, devices: BOTH },
  "service-protocols": { icon: FileSignature, devices: DESKTOP },
  "approvals-audit": { icon: CheckCheck, devices: DESKTOP },
  "forms-list": { icon: FileStack, devices: BOTH },
  "form-stats": { icon: PieChart, devices: BOTH },
  "form-builder": { icon: SquarePen, devices: DESKTOP },
  "purchase-invoices": { icon: FileInput, devices: BOTH },
  "diary-calendar": { icon: CalendarSearch, devices: BOTH },
  "fixed-assets": { icon: Building2, devices: BOTH },
  "tool-assignments": { icon: UserCheck, devices: BOTH },
  "workzone-tools": { icon: Briefcase, devices: BOTH },
  "doc-library": { icon: Library, devices: BOTH },
  "doc-list": { icon: ListIcon, devices: DESKTOP },
  "doc-detail": { icon: FileSearch, devices: DESKTOP },
  "project-files": { icon: Images, devices: DESKTOP },
  "website-overview": { icon: Globe, devices: BOTH },
  "website-editor": { icon: PenTool, devices: DESKTOP },
  "website-preview": { icon: MonitorSmartphone, devices: BOTH },
  "website-templates": { icon: LayoutTemplate, devices: BOTH },
  "shared-projects": { icon: Share2, devices: BOTH },
  "shared-project": { icon: Handshake, devices: BOTH },
  "shared-material": { icon: PackageCheck, devices: DESKTOP },
  "project-subcontractors": { icon: FolderOpen, devices: DESKTOP },
} satisfies Record<string, ShowcaseScreenDef>;

export type ShowcaseScreenId = keyof typeof SHOWCASE_SCREENS;

/**
 * Which screens each page shows, in order: the first half goes in the left
 * column, the rest in the right one. `home` is the homepage; the other keys are
 * canonical module slugs (the page heading lives under
 * `sections.productShowcase.pages.<key>`).
 */
export const SHOWCASE_PAGES = {
  home: [
    "time-tracking",
    "projects",
    "planning",
    "quality",
    "materials",
    "expenses",
    "diary",
    "invoicing",
  ],
  "projektove-riadenie": [
    "projects",
    "time-tracking",
    "customer",
    "gantt",
    "kanban",
    "unbilled",
  ],
  "online-fakturacia": [
    "quote",
    "invoicing",
    "invoice-pdf",
    "receivables",
    "aging",
    "bank",
  ],
  uctovnictvo: ["accounting", "bank", "ocr", "expenses", "receivables"],
  "reporting-analytika": [
    "dashboard",
    "project-stats",
    "budget-vs-actuals",
    "forecast-eac",
    "receivables",
    "aging",
  ],
  "kontrola-nakladov": [
    "budget-vs-actuals",
    "project-economy",
    "forecast-eac",
    "change-orders",
    "work-summaries",
    "projects",
  ],
  kalkulacie: [
    "boq-budget",
    "quote",
    "quote-pdf",
    "budget-vs-actuals",
    "change-orders",
  ],
  "vykaz-prace-a-evidencia-dochadzky": [
    "time-tracking",
    "workzone-home",
    "attendance-qr",
    "hr-timetracking",
    "leaves",
  ],
  "crm-system-prehlad-zakaznikov": [
    "customer",
    "crm-pipeline",
    "inquiries",
    "crm-follow-ups",
    "quotes-list",
    "receivables",
  ],
  "riadenie-timov": [
    "employees",
    "crews",
    "leaves",
    "workzone-home",
    "workzone-requests",
    "hr-timetracking",
  ],
  "vozidla-trasy": [
    "fleet-overview",
    "fleet-vehicles",
    "fleet-trips",
    "fleet-events",
    "vehicle-detail",
  ],
  "cenove-ponuky": [
    "quote",
    "quotes-list",
    "quote-pdf",
    "crm-pipeline",
    "change-orders",
  ],
  "sklad-materialy": [
    "materials",
    "stock-overview",
    "stock-locations",
    "stock-movements",
    "stock-counts",
    "reorder-suggestions",
  ],
  "kalendar-planovanie": [
    "planning",
    "gantt",
    "project-calendar",
    "calendar-events",
    "leaves",
  ],
  "bezpecnost-kvalita": [
    "quality",
    "qc-project",
    "inspections",
    "bozp-overview",
    "bozp-trainings",
  ],
  "mobilna-aplikacia": [
    "workzone-home",
    "time-tracking",
    "on-site",
    "expenses",
    "attendance-qr",
    "install-app",
  ],
  bankovnictvo: [
    "bank",
    "bank-accounts",
    "bank-connections",
    "bank-import-history",
    "receivables",
  ],
  mzdy: ["payroll", "hr-timetracking", "leaves", "employees"],
  "sprava-techniky": [
    "machines-overview",
    "machine-detail",
    "machine-planning",
    "tool-maintenance",
    "tools-dashboard",
    "tool-catalog",
  ],
  servis: [
    "service-today",
    "service-board",
    "service-week",
    "service-van",
    "service-protocols",
    "unbilled",
  ],
  schvalenia: [
    "ocr",
    "approvals-audit",
    "leaves",
    "expenses",
    "workzone-requests",
  ],
  formulare: ["forms-list", "form-stats", "form-builder", "inquiries"],
  "firemne-vydavky": ["expenses", "purchase-invoices", "ocr", "accounting"],
  "stavebny-dennik": [
    "diary",
    "diary-calendar",
    "on-site",
    "qc-project",
    "quality",
  ],
  "spracovanie-dokladov": [
    "ocr",
    "purchase-invoices",
    "approvals-audit",
    "bank",
    "accounting",
  ],
  "sledovanie-materialov": [
    "materials",
    "stock-movements",
    "reorder-suggestions",
    "purchase-orders",
    "project-economy",
  ],
  "majetok-firmy": [
    "fixed-assets",
    "tools-dashboard",
    "tool-assignments",
    "machine-detail",
    "workzone-tools",
    "tool-maintenance",
  ],
  "sprava-dokumentov": [
    "doc-library",
    "doc-detail",
    "project-files",
    "doc-list",
  ],
  "firemny-web": [
    "website-overview",
    "website-editor",
    "website-preview",
    "website-templates",
    "inquiries",
  ],
  subkontraktori: [
    "shared-projects",
    "shared-project",
    "project-subcontractors",
    "shared-material",
  ],
} satisfies Record<string, readonly ShowcaseScreenId[]>;

export type ShowcasePage = keyof typeof SHOWCASE_PAGES;

/** Module slugs that share another page's showcase. */
const PAGE_ALIASES: Record<string, ShowcasePage> = {
  "fakturacny-modul": "online-fakturacia",
};

/** The showcase for a module slug, or null when that module has none yet. */
export function showcasePageFor(slug: string): ShowcasePage | null {
  if (slug in SHOWCASE_PAGES) return slug as ShowcasePage;
  return PAGE_ALIASES[slug] ?? null;
}

/** Autoplay only makes sense when the device has a next screen to move to. */
export function showcaseCanCycle(page: ShowcasePage, device: ShowcaseDevice): boolean {
  return screensFor(page, device).length > 1;
}

export const SHOWCASE_SETTINGS = {
  defaultDevice: "mobile" as ShowcaseDevice,
  /** Cycle through the screens on its own until the visitor picks one. */
  autoplay: true,
  autoplayMs: 5000,
  /** Screen change and device swap. */
  transitionMs: 350,
};

export const SHOWCASE_LANGUAGES = ["sk", "cs", "da", "en"] as const;

function screenBase(
  language: string,
  id: ShowcaseScreenId,
  device: ShowcaseDevice,
): string {
  const lang = (SHOWCASE_LANGUAGES as readonly string[]).includes(language)
    ? language
    : "en";
  return `/images/showcase/${lang}/${id}-${device}`;
}

/** `src`, plus a smaller desktop variant so phones do not download the 1200 px file. */
export function showcaseImage(
  language: string,
  id: ShowcaseScreenId,
  device: ShowcaseDevice,
) {
  const base = screenBase(language, id, device);
  if (device === "mobile") return { src: `${base}.webp` };
  return {
    src: `${base}.webp`,
    srcSet: `${base}-640.webp 640w, ${base}.webp 1200w`,
    sizes: "(min-width: 1280px) 760px, (min-width: 768px) 90vw, 100vw",
  };
}

export function screensFor(
  page: ShowcasePage,
  device: ShowcaseDevice,
): ShowcaseScreenId[] {
  return (SHOWCASE_PAGES[page] as readonly ShowcaseScreenId[]).filter((id) =>
    SHOWCASE_SCREENS[id].devices.includes(device),
  );
}

/** Devices the page has at least one screen for. */
export function devicesFor(page: ShowcasePage): ShowcaseDevice[] {
  return SHOWCASE_DEVICES.filter(
    (device) => screensFor(page, device).length > 0,
  );
}

/** Keeps the chosen screen when the new device has it, else falls back to the first one there. */
export function screenAfterDeviceChange(
  page: ShowcasePage,
  current: ShowcaseScreenId,
  device: ShowcaseDevice,
): ShowcaseScreenId {
  const available = screensFor(page, device);
  return available.includes(current) ? current : (available[0] ?? current);
}
